import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { memo, useCallback, useMemo, useState } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { useInView } from "@/core/hooks";
import { formatActivityDate, formatDate } from "@/core/utils/date";
import { formatMoney, formatMoneyCents } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import {
  SPIFF_RULE_EVENTS,
  SPIFF_RULE_EVENTS_SHORT_LABELS,
} from "@/spiff-rules/constants";
import { SpiffRule } from "@/spiff-rules/spiff-rule";
import {
  SPIFF_EVENT_BADGE_VARIANTS,
  SPIFF_EVENT_BG_COLORS,
  SPIFF_EVENT_BORDER_COLORS,
} from "@/spiffs/constants";
import { useReinstateSpiff } from "@/spiffs/query";
import {
  IAttributedUserWithSpiff,
  IEnrichedActivityFeedItem,
  IActivityFeedPricebookItemDetails,
} from "./activity-feed.d";
import { ActivityText } from "./activity-text";
import { ACTIVITY_CARD_BOTTOM_PADDING } from "./card-spacing-constants";
import { CreateAttributionSpiffModal } from "./create-attribution-spiff-modal";
import { ActivityDetailCard } from "./detail-cards";
import ServiceTitanLogoWithText from "@/assets/images/service-titan-logo-with-text.png";

interface ActivityItemProps {
  activity: IEnrichedActivityFeedItem;
  onOpenEventModal?: (activity: IEnrichedActivityFeedItem) => void;
  tglRule?: SpiffRule;
}

export const ActivityItem = memo(
  ({ activity, onOpenEventModal, tglRule }: ActivityItemProps) => {
    const [itemRef, inView] = useInView({
      rootMargin: "500px", // Trigger when the item is 500px from entering the viewport
      triggerOnce: true,
      delay: 150, // Wait 150ms before triggering fetch (prevents fast-scroll requests)
    });
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSpiffModalOpen, setIsSpiffModalOpen] = useState(false);
    const [selectedUserForSpiff, setSelectedUserForSpiff] =
      useState<IAttributedUserWithSpiff | null>(null);

    const { mutate: reinstateSpiff } = useReinstateSpiff(activity.business_id);

    const {
      displayAmount,
      primarySpiff,
      attribution,
      attributedUsersWithSpiffs,
      unDismissedUsersWithSpiffs,
    } = activity;

    const undismissedSpiffCount = unDismissedUsersWithSpiffs.length;

    const primaryUndismissedSpiff =
      undismissedSpiffCount === 1
        ? {
            userId: unDismissedUsersWithSpiffs[0].userId,
            displayName: unDismissedUsersWithSpiffs[0].displayIdentification,
            isCurrentUser: unDismissedUsersWithSpiffs[0].isCurrentUser,
          }
        : primarySpiff;

    const displayName = useMemo(
      () => activity.details_json.display_identification,
      [activity.details_json.display_identification],
    );

    const attributionCount = attributedUsersWithSpiffs.length;

    const badgeVariant = useMemo(
      () =>
        SPIFF_EVENT_BADGE_VARIANTS[
          activity.spiff_event_name as keyof typeof SPIFF_EVENT_BADGE_VARIANTS
        ] || "gray",
      [activity.spiff_event_name],
    );

    const eventBgColor = useMemo(
      () =>
        SPIFF_EVENT_BG_COLORS[
          activity.spiff_event_name as keyof typeof SPIFF_EVENT_BG_COLORS
        ] || "bg-gray-10",
      [activity.spiff_event_name],
    );

    const eventBorderColor = useMemo(
      () =>
        SPIFF_EVENT_BORDER_COLORS[
          activity.spiff_event_name as keyof typeof SPIFF_EVENT_BORDER_COLORS
        ] || "border-badge-gray",
      [activity.spiff_event_name],
    );

    const handleReinstate = useCallback(
      (spiffId: string) => {
        reinstateSpiff({ spiffId });
      },
      [reinstateSpiff],
    );

    const percentage_base_amount_cents = (
      activity.details_json as IActivityFeedPricebookItemDetails
    ).percentage_base_amount_cents;

    const tglErrorMessage = useMemo(() => {
      if (!tglRule) {
        return "You must create a spiff rule for Tech Generated Leads before creating a spiff.";
      }
      if (!percentage_base_amount_cents) {
        return "Error, no base amount found.";
      }
      return null;
    }, [tglRule, percentage_base_amount_cents]);

    const isDisabled = tglErrorMessage !== null;

    return (
      <div ref={itemRef} className="group w-[650px]">
        <Card className="w-full overflow-hidden rounded-2xl">
          <div className="flex items-center justify-between">
            <div
              className={cn(
                "inline-block w-[108px] rounded-br-lg py-2 text-center",
                eventBgColor,
              )}
            >
              <Typography variant="captionW1">
                {SPIFF_RULE_EVENTS_SHORT_LABELS[activity.spiff_event_name]}
              </Typography>
            </div>

            {activity.spiff_event_name ===
              SPIFF_RULE_EVENTS.pricebook_item_sale && (
              <img
                src={ServiceTitanLogoWithText}
                alt="ServiceTitan"
                className="mr-4 mt-1.5 h-4"
              />
            )}
          </div>
          <CardContent
            className={cn(
              "p-4 pt-3",
              ACTIVITY_CARD_BOTTOM_PADDING[activity.spiff_event_name],
            )}
          >
            <Stack
              direction="row"
              justify="between"
              align="start"
              gap="xl"
              fullWidth
            >
              <Stack className="gap-0.5">
                <ActivityText
                  eventName={activity.spiff_event_name}
                  displayName={displayName}
                  spiffedUser={primaryUndismissedSpiff?.displayName || null}
                  spiffCount={undismissedSpiffCount}
                  attributedUser={attribution?.displayName || null}
                />
                <Stack direction="row" align="baseline" gap="sm">
                  <Typography variant="captionW1" textColor="tertiary">
                    {formatActivityDate(
                      activity.timestamp,
                      activity.spiff_event_name,
                    )}
                  </Typography>
                  {attributionCount > 0 && (
                    <>
                      <Typography variant="captionW1" textColor="tertiary">
                        {"•"}
                      </Typography>
                      <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-expanded={isExpanded}
                        className="flex items-center gap-1 text-xs font-medium text-tertiary-text hover:text-secondary-text"
                      >
                        Details
                        <ChevronDownIcon
                          className={cn(
                            "h-4 w-4 transition-transform duration-300",
                            isExpanded && "rotate-180",
                          )}
                        />
                      </button>
                    </>
                  )}
                </Stack>
              </Stack>
              {displayAmount > 0 ? (
                <Badge
                  variant={badgeVariant}
                  className="font-montserrat shrink-0 font-semibold"
                >
                  {formatMoney(displayAmount)}
                </Badge>
              ) : (
                activity.spiff_event_name ===
                  SPIFF_RULE_EVENTS.tech_generated_lead &&
                attributedUsersWithSpiffs.length === 0 && (
                  <Tooltip>
                    <TooltipTrigger asDiv>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onOpenEventModal?.(activity)}
                        disabled={isDisabled}
                      >
                        Create Spiff
                      </Button>
                    </TooltipTrigger>
                    {isDisabled && (
                      <TooltipContent>{tglErrorMessage}</TooltipContent>
                    )}
                  </Tooltip>
                )
              )}
            </Stack>
            {attributionCount > 0 && (
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300 ease-in-out",
                  isExpanded
                    ? "mt-4 max-h-[2000px] opacity-100"
                    : "max-h-0 opacity-0",
                )}
              >
                <Stack gap="md" fullWidth>
                  {attributedUsersWithSpiffs.map((attributedUser) => {
                    const userName = attributedUser.isCurrentUser
                      ? "You"
                      : attributedUser.displayIdentification;
                    return (
                      <Stack
                        direction="row"
                        align="center"
                        justify="between"
                        fullWidth
                        key={attributedUser.userId}
                        className={cn("border-l-4 py-2 pl-4", eventBorderColor)}
                      >
                        <Stack
                          gap="xs"
                          className={cn("opacity-100", {
                            "opacity-30": attributedUser.spiff?.dismissedAt,
                          })}
                        >
                          <Typography variant="subtitleW3">
                            {userName}{" "}
                            {attributedUser.spiff?.dismissedAt && "(DISMISSED)"}
                          </Typography>
                          <Typography variant="captionW1" textColor="tertiary">
                            {attributedUser.spiff
                              ? `Spiffed ${formatDate(attributedUser.spiff.createdAt)}`
                              : "Attributed"}
                          </Typography>
                        </Stack>
                        {attributedUser.spiff?.dismissedAt && (
                          <Stack className="w-full flex-row items-center justify-end gap-10">
                            <Button
                              onClick={() =>
                                handleReinstate(attributedUser.spiff!.id)
                              }
                              size="xs"
                              className="h-7"
                              variant="tertiary"
                            >
                              Reinstate
                            </Button>
                          </Stack>
                        )}

                        {attributedUser.spiff ? (
                          <Badge
                            variant={badgeVariant}
                            className={cn(
                              "font-montserrat font-semibold",
                              attributedUser.spiff.dismissedAt && "opacity-30",
                            )}
                          >
                            {formatMoneyCents(attributedUser.spiff.amountCents)}
                          </Badge>
                        ) : (
                          <Button
                            variant="tertiary"
                            size="xs"
                            onClick={() => {
                              setSelectedUserForSpiff(attributedUser);
                              setIsSpiffModalOpen(true);
                            }}
                          >
                            Create Spiff
                          </Button>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </div>
            )}
          </CardContent>
        </Card>
        <ActivityDetailCard
          activity={activity}
          inView={inView}
          onOpenEventModal={onOpenEventModal}
        />
        <CreateAttributionSpiffModal
          open={isSpiffModalOpen}
          onOpenChange={setIsSpiffModalOpen}
          activity={activity}
          attributedUser={selectedUserForSpiff}
        />
      </div>
    );
  },
);

ActivityItem.displayName = "ActivityItem";
